/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';
import { RowMenu, SettingGroup, SettingRow } from '../../components/SettingGroups';

import { useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';
import BedDialog from './BedDialog';
import RoomDialog from './RoomDialog';

export function SettingRoom() {
  const rooms = useLoader(() => settingsApi.rooms(), []);
  const [creating, setCreating] = useState(null);   // { kind, roomId } while open
  const [editing, setEditing] = useState(null);     // { kind, row, roomId }
  const [showInactive, setShowInactive] = useState(false);

  /* `roomId` is passed in rather than read off the bed: a bed arrives inside the
     room that holds it and carries no room of its own. */
  const bedRow = (b, roomId, reload) => (
    <SettingRow
      key={b.id}
      /* 30px, measured: their bed row's grip is a 30x30 glyph, and it — not the
         24px label — is what makes the row 58px rather than 52. */
      gripSize={30}
      label={(
        <button
          type="button"
          className="row-open"
          onClick={() => setEditing({ kind: 'bed', row: b, roomId })}
        >
          <span data-no-translate>{b.name || `เตียง ${b.code}`}</span>
        </button>
      )}
      trailing={(
        <span className="bed-row-badges">
          <span className={`badge ${b.isActive !== false ? 'badge-green' : 'badge-grey'}`}>
            {b.isActive !== false ? 'ใช้งาน' : 'ปิดใช้งาน'}
          </span>
          <span className="badge badge-grey">{b.capacity} ที่</span>
        </span>
      )}
      menu={(
        <RowMenu
          items={[
            { label: 'แก้ไข', onClick: () => setEditing({ kind: 'bed', row: b, roomId }) },
            b.isActive !== false
              ? { label: 'ปิดใช้งาน', danger: true,
                  onClick: async () => { await settingsApi.remove('beds', b.id); reload(); } }
              : { label: 'เปิดใช้งาน',
                  onClick: async () => { await settingsApi.restore('beds', b.id); reload(); } },
          ]}
        />
      )}
    />
  );

  const roomGroup = (r) => {
    const visibleBeds = r.beds.filter((bed) => showInactive || bed.isActive !== false);
    return (
    <SettingGroup
      key={r.id}
      title={(
        <span className="room-title-with-status">
          <span data-no-translate>{r.name_th || r.code}</span>
          <span className={`badge ${r.is_active !== false ? 'badge-green' : 'badge-grey'}`}>
            {r.is_active !== false ? 'ใช้งาน' : 'ปิดใช้งาน'}
          </span>
        </span>
      )}
      count={visibleBeds.length}
      countUnit="เตียง"
      empty="ยังไม่มีเตียงในห้องนี้"
      menu={(
        <RowMenu
          items={[
            { label: 'แก้ไขห้อง', onClick: () => setEditing({ kind: 'room', row: r }) },
            r.is_active !== false && { label: 'เพิ่มเตียง',
              onClick: () => setCreating({ kind: 'bed', roomId: r.id }) },
            r.is_active !== false
              ? { label: 'ปิดใช้งาน', danger: true,
                  onClick: async () => { await settingsApi.remove('rooms', r.id); rooms.reload(); } }
              : { label: 'เปิดใช้งาน',
                  onClick: async () => { await settingsApi.restore('rooms', r.id); rooms.reload(); } },
          ]}
        />
      )}
    >
      {visibleBeds.map((b) => bedRow(b, r.id, rooms.reload))}
    </SettingGroup>
    );
  };

  return (
    <SettingsPage
      action="สร้างห้อง"
      actionMenu={[
        { label: 'เตียง', onClick: () => setCreating({ kind: 'bed' }) },
        { label: 'ห้องนวด', onClick: () => setCreating({ kind: 'room' }) },
      ]}
      showInactive={showInactive}
      onShowInactive={setShowInactive}
    >
      <Panel loader={rooms}>
        {(d) => {
          const activeRooms = d.rooms.filter((r) => r.is_active !== false);
          const inactiveRooms = d.rooms.filter((r) => r.is_active === false);
          const unassignedBeds = d.unassignedBeds.filter(
            (bed) => showInactive || bed.isActive !== false
          );
          return (
            <>
              {activeRooms.map(roomGroup)}

              {unassignedBeds.length > 0 && (
                <SettingGroup
                  title="เตียงที่ยังไม่ระบุห้อง"
                  count={unassignedBeds.length}
                  countUnit="เตียง"
                >
                  {unassignedBeds.map((b) => bedRow(b, '', rooms.reload))}
                </SettingGroup>
              )}

              {showInactive && inactiveRooms.map(roomGroup)}
            </>
          );
        }}
      </Panel>

      {creating?.kind === 'room' && (
        <RoomDialog
          onSubmit={async (v) => {
            await settingsApi.createRoom({
              code: v.code,
              nameTh: v.nameTh,
              isPrivate: v.isPrivate,
              isActive: v.isActive,
            });
            rooms.reload();
            setCreating(null);
          }}
          onClose={() => setCreating(null)}
        />
      )}

      {editing?.kind === 'room' && (
        <RoomDialog
          room={editing.row}
          onSubmit={async (v) => {
            await settingsApi.update('rooms', editing.row.id, {
              code: v.code,
              nameTh: v.nameTh,
              isPrivate: v.isPrivate,
              isActive: v.isActive,
            });
            rooms.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {(creating?.kind === 'bed' || editing?.kind === 'bed') && (
        <BedDialog
          bed={editing?.row}
          roomId={editing ? editing.roomId : creating.roomId}
          rooms={rooms.data?.rooms || []}
          onSubmit={async (payload) => {
            if (editing) await settingsApi.update('beds', editing.row.id, payload);
            else await settingsApi.createBed(payload);
            rooms.reload();
            setCreating(null);
            setEditing(null);
          }}
          onClose={() => { setCreating(null); setEditing(null); }}
        />
      )}
    </SettingsPage>
  );
}
